// Inloggningen med Cognito (ADR 0083): e-post och lösenord, ett eget lösenord
// för den som är inbjuden, och MFA med en app för engångskoder. Första gången
// registreras appen med en QR-kod. Glömt lösenordet ger en kod med e-post.
// Efter koden slipper webbläsaren den i 30 dagar, om användaren vill (ADR 0108).
import {
  CognitoFel,
  felText,
  KOM_IHAG_DAGAR,
  type Cognito,
  type Steg,
  type Tokens,
} from '@leadledger/cognito';
import QRCode from 'qrcode';
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';

type Vy =
  | { typ: 'losenord' }
  | { typ: 'nytt-losenord'; session: string }
  | { typ: 'mfa-kod'; session: string }
  | { typ: 'mfa-registrera'; session: string; hemlighet: string; otpauth: string }
  | { typ: 'glomt' }
  | { typ: 'glomt-kod' };

const MINSTA_LOSENORD = 12;
const SESSION_SLUT = felText('NotAuthorizedException', 'session');

function Falt({
  etikett,
  hjalp,
  ...input
}: { etikett: string; hjalp?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className="falt">
      <label className="etikett" htmlFor={id}>
        {etikett}
      </label>
      <input
        id={id}
        className="in"
        required
        {...(hjalp ? { 'aria-describedby': `${id}-hjalp` } : {})}
        {...input}
      />
      {hjalp && (
        <div className="hjalp" id={`${id}-hjalp`}>
          {hjalp}
        </div>
      )}
    </div>
  );
}

function QrKod({ text }: { text: string }) {
  const [svg, setSvg] = useState<string | null>(null);
  useEffect(() => {
    let aktiv = true;
    void QRCode.toString(text, { type: 'svg', margin: 1, width: 180 }).then((s) => {
      if (aktiv) setSvg(s);
    });
    return () => {
      aktiv = false;
    };
  }, [text]);
  if (!svg) return null;
  return (
    <img
      className="qr"
      width={180}
      height={180}
      alt="QR-kod att skanna i appen för engångskoder"
      src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`}
    />
  );
}

export function CognitoInloggning({
  cognito,
  onKlar,
}: {
  cognito: Cognito;
  onKlar: (tokens: Tokens) => void;
}) {
  const [vy, setVy] = useState<Vy>({ typ: 'losenord' });
  const [epost, setEpost] = useState('');
  const [losenord, setLosenord] = useState('');
  const [kod, setKod] = useState('');
  const [fel, setFel] = useState<string | null>(null);
  const [kvitto, setKvitto] = useState<string | null>(null);
  const [vantar, setVantar] = useState(false);
  // Webbläsaren slipper koden i 30 dagar, om inte användaren säger nej (ADR 0108).
  const [kommIhag, setKommIhag] = useState(true);

  function nasta(steg: Steg) {
    setKod('');
    if (steg.typ === 'inloggad') onKlar(steg.tokens);
    else setVy(steg);
  }

  async function kor(e: FormEvent, gor: () => Promise<void>) {
    e.preventDefault();
    setFel(null);
    setKvitto(null);
    setVantar(true);
    try {
      await gor();
    } catch (err) {
      setFel(err instanceof CognitoFel ? err.message : 'Inloggningen misslyckades. Försök igen.');
      // Stegen gäller bara några minuter. Därefter börjar man om.
      if (err instanceof CognitoFel && err.message === SESSION_SLUT) setVy({ typ: 'losenord' });
    } finally {
      setVantar(false);
    }
  }

  const borjaOm = () => {
    setFel(null);
    setKvitto(null);
    setLosenord('');
    setKod('');
    setVy({ typ: 'losenord' });
  };

  const meddelanden = (
    <>
      {fel && (
        <div className="meddelande" role="alert">
          {fel}
        </div>
      )}
      {kvitto && (
        <div className="kvitto" role="status">
          {kvitto}
        </div>
      )}
    </>
  );

  const knapp = (text: string, vantarText: string): ReactNode => (
    <button type="submit" className="btn btn-p" disabled={vantar} aria-busy={vantar}>
      {vantar ? vantarText : text}
    </button>
  );

  const tillbaka = (
    <button type="button" className="lank-knapp" onClick={borjaOm}>
      Tillbaka till inloggningen
    </button>
  );

  const kodFalt = (etikett: string) => (
    <Falt
      etikett={etikett}
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9 ]{6,7}"
      maxLength={7}
      value={kod}
      onChange={(e) => setKod(e.target.value)}
    />
  );

  const kommIhagRuta = (
    <label className="avtal-kryss">
      <input type="checkbox" checked={kommIhag} onChange={(e) => setKommIhag(e.target.checked)} />
      <span>
        Fråga inte efter kod i den här webbläsaren på {KOM_IHAG_DAGAR} dagar. Låt bli på en dator
        som andra använder.
      </span>
    </label>
  );

  switch (vy.typ) {
    case 'losenord':
      return (
        <form
          className="inloggning-form"
          onSubmit={(e) =>
            void kor(e, async () => nasta(await cognito.loggaIn(epost.trim(), losenord)))
          }
        >
          <h1>Logga in</h1>
          {meddelanden}
          <Falt
            etikett="Jobbmejl"
            type="email"
            autoComplete="username"
            value={epost}
            onChange={(e) => setEpost(e.target.value)}
          />
          <Falt
            etikett="Lösenord"
            type="password"
            autoComplete="current-password"
            value={losenord}
            onChange={(e) => setLosenord(e.target.value)}
          />
          {knapp('Logga in', 'Loggar in …')}
          <button
            type="button"
            className="lank-knapp"
            onClick={() => {
              setFel(null);
              setVy({ typ: 'glomt' });
            }}
          >
            Glömt lösenordet?
          </button>
        </form>
      );

    case 'nytt-losenord':
      return (
        <form
          className="inloggning-form"
          onSubmit={(e) =>
            void kor(e, async () =>
              nasta(await cognito.nyttLosenord(vy.session, epost.trim(), losenord)),
            )
          }
        >
          <h1>Välj ett eget lösenord</h1>
          <p>Du loggade in med lösenordet från inbjudan. Välj nu ett eget.</p>
          {meddelanden}
          <Falt
            etikett="Nytt lösenord"
            type="password"
            autoComplete="new-password"
            minLength={MINSTA_LOSENORD}
            hjalp={`Minst ${MINSTA_LOSENORD} tecken. En mening fungerar bra.`}
            value={losenord}
            onChange={(e) => setLosenord(e.target.value)}
          />
          {knapp('Spara och fortsätt', 'Sparar …')}
        </form>
      );

    case 'mfa-registrera':
      return (
        <form
          className="inloggning-form"
          onSubmit={(e) =>
            void kor(e, async () =>
              nasta(await cognito.registreraMfa(vy.session, epost.trim(), kod, kommIhag)),
            )
          }
        >
          <h1>Skydda kontot med en kod</h1>
          <p>
            Inloggningen kräver en kod från en app för engångskoder, till exempel Microsoft
            Authenticator, Google Authenticator eller 1Password. Skanna QR-koden med appen.
          </p>
          <QrKod text={vy.otpauth} />
          <p className="hjalp">
            Går det inte att skanna? Lägg till kontot för hand med nyckeln{' '}
            <code className="nyckel">{vy.hemlighet.replace(/(.{4})/g, '$1 ').trim()}</code>
          </p>
          {meddelanden}
          {kodFalt('Sexsiffrig kod från appen')}
          {kommIhagRuta}
          {knapp('Aktivera och logga in', 'Kontrollerar …')}
        </form>
      );

    case 'mfa-kod':
      return (
        <form
          className="inloggning-form"
          onSubmit={(e) =>
            void kor(e, async () =>
              nasta(await cognito.mfaKod(vy.session, epost.trim(), kod, kommIhag)),
            )
          }
        >
          <h1>Ange koden</h1>
          <p>Öppna appen för engångskoder och skriv koden för LeadLedger.</p>
          {meddelanden}
          {kodFalt('Sexsiffrig kod')}
          {kommIhagRuta}
          {knapp('Logga in', 'Loggar in …')}
          {tillbaka}
        </form>
      );

    case 'glomt':
      return (
        <form
          className="inloggning-form"
          onSubmit={(e) =>
            void kor(e, async () => {
              await cognito.glomtLosenord(epost.trim());
              setLosenord('');
              setVy({ typ: 'glomt-kod' });
            })
          }
        >
          <h1>Glömt lösenordet</h1>
          <p>Vi skickar en kod till din e-post. Med den väljer du ett nytt lösenord.</p>
          {meddelanden}
          <Falt
            etikett="Jobbmejl"
            type="email"
            autoComplete="username"
            value={epost}
            onChange={(e) => setEpost(e.target.value)}
          />
          {knapp('Skicka kod', 'Skickar …')}
          {tillbaka}
        </form>
      );

    case 'glomt-kod':
      return (
        <form
          className="inloggning-form"
          onSubmit={(e) =>
            void kor(e, async () => {
              await cognito.valjLosenordMedKod(epost.trim(), kod, losenord);
              setLosenord('');
              setKod('');
              setVy({ typ: 'losenord' });
              setKvitto('Lösenordet är bytt. Logga in med det nya.');
            })
          }
        >
          <h1>Välj ett nytt lösenord</h1>
          <p>Om adressen finns hos oss har vi skickat en kod till {epost.trim()}.</p>
          {meddelanden}
          {kodFalt('Koden från mejlet')}
          <Falt
            etikett="Nytt lösenord"
            type="password"
            autoComplete="new-password"
            minLength={MINSTA_LOSENORD}
            hjalp={`Minst ${MINSTA_LOSENORD} tecken.`}
            value={losenord}
            onChange={(e) => setLosenord(e.target.value)}
          />
          {knapp('Spara lösenordet', 'Sparar …')}
          {tillbaka}
        </form>
      );
  }
}
